import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { AUTH_TOKEN } from 'constants/AuthConstant';
import FirebaseService from 'services/FirebaseService';
import { API_BASE_URL } from 'constants/ApiConstant';
import axios from 'axios';
import { toast } from 'react-toastify';
import axiosInstance from 'utils/axiosInstance';


export const initialState = {
	loading: false,
	message: '',
	showMessage: false,
	redirect: '',
	token: localStorage.getItem(AUTH_TOKEN) || null,
	userMenu: [],
	permissions: [],
};

export const fetchMenu = createAsyncThunk(
	'auth/fetchMenu',
	async (_, { rejectWithValue }) => {
		try {
			const token = localStorage.getItem(AUTH_TOKEN);
			const response = await axiosInstance.get(`${API_BASE_URL}/api/admin/menuList`, {
				headers: {
					Authorization: `Bearer ${token}`,
				},
			});

			const { menu, permissions } = response.data.admin;

			if (!menu || menu.length === 0) {
				return rejectWithValue('You do not have any permissions assigned. Please contact the administrator.');
			}

			return { menu, permissions };
		} catch (err) {
			return rejectWithValue(err.response?.data?.message || 'Failed to fetch menu');
		}
	}
);

export const signIn = createAsyncThunk('auth/signIn', async (data, { rejectWithValue }) => {
	const { email, password } = data;

	const formData = {
		email: email,
		password: password,
	};

	try {
		const response = await axios({
			url: `${API_BASE_URL}/api/admin/login`,
			method: "POST",
			headers: {
				"Content-Type": "application/json"
			},
			data: formData,
		});

		const token = response.data.admin.admin_user.au_token;
		const userMenu = response.data.admin.menu;

		// Check if userMenu is empty
		if (!userMenu || userMenu.length === 0) {
			return rejectWithValue('You do not have any permissions assigned. Please contact the administrator.');
		}
		localStorage.setItem(AUTH_TOKEN, token);
		// const resData = await FirebaseService.signInEmailRequest(email, password);
		const profile_details = {
			au_first_name: response.data.admin.admin_user.au_first_name,
			au_last_name: response.data.admin.admin_user.au_last_name,
			au_profile_image: response.data.admin.admin_user.au_profile_image,
		}
		// localStorage.setItem('user_menu', JSON.stringify(response.data.admin.menu));
		localStorage.setItem('user_name', JSON.stringify(response.data.admin.admin_user));
		localStorage.setItem('permissions', JSON.stringify(response.data.admin.permissions));
		localStorage.setItem('profile_details', JSON.stringify(profile_details));
		return token;

	} catch (err) {
		return rejectWithValue(err.response?.data?.error || 'Login failed');
	}
});

export const signUp = createAsyncThunk('auth/signUp', async (data, { rejectWithValue }) => {
	const { email, password } = data
	try {
		const response = await FirebaseService.signUpEmailRequest(email, password);
		if (response.user) {
			const token = response.user.refreshToken;
			localStorage.setItem(AUTH_TOKEN, response.user.refreshToken);
			return token;
		} else {
			return rejectWithValue(response.message?.replace('Firebase: ', ''));
		}
	} catch (err) {
		return rejectWithValue(err.message || 'Error')
	}
})

export const signOut = createAsyncThunk('auth/signOut', async () => {
	const response = await FirebaseService.signOutRequest();
	localStorage.removeItem(AUTH_TOKEN);
	localStorage.removeItem('user_menu');
	localStorage.removeItem('permissions');
	localStorage.removeItem('user_name');
	localStorage.removeItem('profile_details');
	return response.data
})

export const signInWithGoogle = createAsyncThunk('auth/signInWithGoogle', async (_, { rejectWithValue }) => {
	const response = await FirebaseService.signInGoogleRequest()
	if (response.user) {
		const token = response.user.refreshToken;
		localStorage.setItem(AUTH_TOKEN, response.user.refreshToken);
		return token;
	} else {
		return rejectWithValue(response.message?.replace('Firebase: ', ''));
	}
})

export const signInWithFacebook = createAsyncThunk('auth/signInWithFacebook', async (_, { rejectWithValue }) => {
	const response = await FirebaseService.signInFacebookRequest()
	if (response.user) {
		const token = response.user.refreshToken;
		localStorage.setItem(AUTH_TOKEN, response.user.refreshToken);
		return token;
	} else {
		return rejectWithValue(response.message?.replace('Firebase: ', ''));
	}
})


export const authSlice = createSlice({
	name: 'auth',
	initialState,
	reducers: {
		authenticated: (state, action) => {
			state.loading = false
			state.redirect = '/'
			state.token = action.payload
		},
		showAuthMessage: (state, action) => {
			state.message = action.payload
			state.showMessage = true
			state.loading = false
		},
		hideAuthMessage: (state) => {
			state.message = ''
			state.showMessage = false
		},
		signOutSuccess: (state) => {
			state.loading = false
			state.token = null
			state.redirect = '/'
		},
		showLoading: (state) => {
			state.loading = true
		},
		signInSuccess: (state, action) => {
			state.loading = false
			state.token = action.payload
		}
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchMenu.pending, (state) => {
				state.loading = true;
			})
			.addCase(fetchMenu.fulfilled, (state, action) => {
				state.loading = false;
				state.userMenu = action.payload.menu;
				state.permissions = action.payload.permissions;
			})
			.addCase(fetchMenu.rejected, (state, action) => {
				state.loading = false;
				state.message = action.payload;
				state.showMessage = true;
			})
			.addCase(signIn.pending, (state) => {
				state.loading = true
			})
			.addCase(signIn.fulfilled, (state, action) => {
				state.loading = false
				state.redirect = '/'
				state.token = action.payload
			})
			.addCase(signIn.rejected, (state, action) => {
				state.message = action.payload
				state.showMessage = true
				state.loading = false
			})
			.addCase(signOut.fulfilled, (state) => {
				state.loading = false
				state.token = null
				state.redirect = '/'
			})
			.addCase(signOut.rejected, (state) => {
				state.loading = false
				state.token = null
				state.redirect = '/'
			})
			.addCase(signUp.pending, (state) => {
				state.loading = true
			})
			.addCase(signUp.fulfilled, (state, action) => {
				state.loading = false
				state.redirect = '/'
				state.token = action.payload
			})
			.addCase(signUp.rejected, (state, action) => {
				state.message = action.payload
				state.showMessage = true
				state.loading = false
			})
			.addCase(signInWithGoogle.pending, (state) => {
				state.loading = true
			})
			.addCase(signInWithGoogle.fulfilled, (state, action) => {
				state.loading = false
				state.redirect = '/'
				state.token = action.payload
			})
			.addCase(signInWithGoogle.rejected, (state, action) => {
				state.message = action.payload
				state.showMessage = true
				state.loading = false
			})
			.addCase(signInWithFacebook.pending, (state) => {
				state.loading = true
			})
			.addCase(signInWithFacebook.fulfilled, (state, action) => {
				state.loading = false
				state.redirect = '/'
				state.token = action.payload
			})
			.addCase(signInWithFacebook.rejected, (state, action) => {
				state.message = action.payload
				state.showMessage = true
				state.loading = false
			})
	},
})

export const {
	authenticated,
	showAuthMessage,
	hideAuthMessage,
	signOutSuccess,
	showLoading,
	signInSuccess
} = authSlice.actions

export default authSlice.reducer;